import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserNurse, faHome, faUsers, faFileMedical, faCalendarAlt, faVideo,
    faFileContract, faComments, faCoins, faIdCard, faSignOutAlt, faBars,
    faCheckCircle, faClock, faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useBaseUrl } from '../../utils/utils';
import { logoutModernAuth } from '../../utils/sessionAuth';

/**
 * Dashboard shell for the Individual Provider dashboard (dashboard_
 * architecture_plan.md C9 / tracker 8.18 slice). Deliberately its own
 * dedicated component rather than another branch bolted onto the shared
 * institution-side dashboard_menu.tsx (878 lines already, imported by
 * dozens of already-deployed institution pages) - this is the "per-
 * dashboard sidebar" pattern 8.18 calls for, applied to the one dashboard
 * being built out next instead of a risky wholesale rewrite.
 */

export const getStoredToken = () =>
    localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('access_token') || '';

/**
 * Shared client for every Phase 9 provider page - one place for the
 * Authorization header and the "no token -> back to login" redirect, so
 * that isn't re-implemented slightly differently in each of 9.1-9.8.
 */
export const useProviderApi = () => {
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();
    const token = getStoredToken();

    useEffect(() => {
        if (!token) navigate('/institution/newlog');
    }, [token, navigate]);

    const api = axios.create({
        baseURL: baseUrl,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    return { api, baseUrl, token };
};

const NAV_ITEMS = [
    { to: '/provider/home', label: 'Overview', icon: faHome, end: true },
    { to: '/provider/patients', label: 'My Patients', icon: faUsers },
    { to: '/provider/records', label: 'Clinical Records', icon: faFileMedical },
    { to: '/provider/appointments', label: 'Appointments', icon: faCalendarAlt },
    { to: '/provider/telehealth', label: 'Consultations', icon: faVideo },
    { to: '/provider/contracts', label: 'Contracts', icon: faFileContract },
    { to: '/provider/messages', label: 'Communications', icon: faComments },
    { to: '/provider/billing', label: 'Billing & Earnings', icon: faCoins },
    { to: '/provider/credentials', label: 'Credentials', icon: faIdCard },
];

const VERIFICATION_META: Record<string, { label: string; color: string; icon: any }> = {
    pending: { label: 'Pending Review', color: '#f7b84b', icon: faClock },
    verified: { label: 'Verified', color: '#0ab39c', icon: faCheckCircle },
    rejected: { label: 'Rejected', color: '#f06548', icon: faExclamationTriangle },
    suspended: { label: 'Suspended', color: '#f06548', icon: faExclamationTriangle },
};

interface MiniProfile {
    full_name: string;
    specialization: string;
    verification_status: string;
}

const ProviderShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();
    const [profile, setProfile] = useState<MiniProfile | null>(null);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        const token = getStoredToken();
        if (!token) return;
        axios.get(`${baseUrl}practitioners/me/`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => setProfile(res.data?.data || null))
            .catch(() => {});
    }, [baseUrl]);

    const handleLogout = async () => {
        await logoutModernAuth(baseUrl);
        navigate('/institution/newlog');
    };

    const verification = profile ? (VERIFICATION_META[profile.verification_status] || VERIFICATION_META.pending) : null;

    return (
        <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafb' }}>
            <aside
                style={{
                    width: 250, flexShrink: 0, background: '#0f766e', color: '#fff',
                    position: 'fixed', top: 0, bottom: 0, left: mobileOpen ? 0 : undefined,
                    display: 'flex', flexDirection: 'column', zIndex: 1000,
                }}
                className={`provider-sidebar ${mobileOpen ? 'open' : ''}`}
            >
                <div style={{ padding: '20px 18px', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <FontAwesomeIcon icon={faUserNurse} size="lg" />
                        <div>
                            <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Uzima Practice</div>
                            <div style={{ fontSize: '0.7rem', opacity: 0.75 }}>Individual Provider</div>
                        </div>
                    </div>
                </div>

                <nav style={{ flex: 1, overflowY: 'auto', padding: '12px 10px' }}>
                    {NAV_ITEMS.map(item => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            onClick={() => setMobileOpen(false)}
                            style={({ isActive }) => ({
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '10px 12px', borderRadius: 10, marginBottom: 3,
                                color: '#fff', textDecoration: 'none', fontSize: '0.84rem',
                                background: isActive ? 'rgba(255,255,255,0.16)' : 'transparent',
                                fontWeight: isActive ? 600 : 400,
                            })}
                        >
                            <FontAwesomeIcon icon={item.icon} style={{ width: 16 }} />
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div style={{ padding: '14px 18px', borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                    <a href="/dashboards" style={{ display: 'block', color: '#fff', fontSize: '0.78rem', opacity: 0.85, marginBottom: 10, textDecoration: 'none' }}>
                        Switch dashboard
                    </a>
                    <button
                        onClick={handleLogout}
                        style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 8, padding: '7px 10px', fontSize: '0.78rem', width: '100%', display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}
                    >
                        <FontAwesomeIcon icon={faSignOutAlt} /> Sign out
                    </button>
                </div>
            </aside>

            <div className="provider-content" style={{ marginLeft: 250, flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <header style={{
                    background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '12px 24px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 900,
                }}>
                    <button
                        className="provider-mobile-toggle"
                        onClick={() => setMobileOpen(o => !o)}
                        style={{ display: 'none', background: 'transparent', border: 'none', fontSize: '1.1rem' }}
                    >
                        <FontAwesomeIcon icon={faBars} />
                    </button>
                    <div>
                        {profile && (
                            <>
                                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Dr. {profile.full_name}</div>
                                <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>{profile.specialization}</div>
                            </>
                        )}
                    </div>
                    {verification && (
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                            borderRadius: 999, fontSize: '0.72rem', fontWeight: 600,
                            background: `${verification.color}1f`, color: verification.color,
                        }}>
                            <FontAwesomeIcon icon={verification.icon} /> {verification.label}
                        </span>
                    )}
                </header>

                <main style={{ flex: 1, padding: '20px 24px' }}>
                    {children}
                </main>
            </div>

            <style>{`
                @media (max-width: 900px) {
                    .provider-sidebar { transform: translateX(-100%); transition: transform 0.2s ease; }
                    .provider-sidebar.open { transform: translateX(0); }
                    .provider-content { margin-left: 0 !important; }
                    .provider-mobile-toggle { display: inline-block !important; }
                }
            `}</style>
        </div>
    );
};

export default ProviderShell;
